import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "../../contexts/snackbar-context";
import type { Document } from "./document-list";
import { AircraftInfoForm } from "./upload/aircraft-info-form";
import { useFileUpload } from "../../hooks/useFileUpload";
import DocumentViewer from "./document-viewer";

interface FailedDocumentModalProps {
  doc: Document | null;
  onClose: () => void;
  refresh: () => Promise<void>;
}

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90vw",
  height: "90vh",
  bgcolor: "#1e1e1e",
  border: "1px solid #fff",
  borderRadius: "8px",
  boxShadow: 24,
  p: 0,
  color: "#fff",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

export const FailedDocumentModal: React.FC<FailedDocumentModalProps> = ({
  doc,
  onClose,
  refresh,
}) => {
  const { showError } = useSnackbar();
  const { updateFiles } = useFileUpload();

  const [tailNumber, setTailNumber] = useState(doc?.tailNumber || "");
  const [documentCategory, setDocumentCategory] = useState(
    doc?.categoryCode || ""
  );
  const [serialNumber, setSerialNumber] = useState("");
  const [aircraftType, setAircraftType] = useState(doc?.aircraftType || "");
  const [documentTitle, setDocumentTitle] = useState(
    doc?.fileName || doc?.title || ""
  );
  const [description, setDescription] = useState(doc?.description || "");

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!doc) return;

    setTailNumber(doc.tailNumber || "");
    setDocumentCategory(doc.categoryCode || "");
    setSerialNumber("");
    setAircraftType(doc.aircraftType || "");
    setDocumentTitle(doc.fileName || doc.title || "");
    setDescription(doc.description || "");
  }, [doc]);

  const handleRetrySubmit = async () => {
    if (!doc) return;

    setSaving(true);

    const payload = {
      documentId: doc.id,
      tailNumber,
      categoryCode: documentCategory,
      serialNumber,
      aircraftType,
      documentTitle,
      description,
    };

    try {
      await updateFiles(payload);
      await refresh();
      onClose();
    } catch {
      showError("Failed to retry document upload.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={!!doc} onClose={onClose}>
      <Box sx={modalStyle}>
        {!doc ? (
          <Typography>No document selected.</Typography>
        ) : (
          <>
            {/* Scrollable content area */}
            <Box sx={{ flex: 1, display: "flex", overflow: "hidden", minHeight: 0 }}>
              {/* LEFT SIDE – FORM */}
              <Box
                sx={{
                  width: "40%",
                  height: "100%",
                  overflowY: "auto",
                  overflowX: "hidden",
                  pr: 2,
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                  minHeight: 0,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    color: "hsl(19 101% 51% / 1)",
                    fontWeight: 700,
                  }}
                >
                  Fix Failed Document Metadata
                </Typography>

                <AircraftInfoForm
                  documentScope="aircraft"
                  setDocumentScope={() => {}}
                  assemblyType=""
                  setAssemblyType={() => {}}
                  assemblyFolder=""
                  setAssemblyFolder={() => {}}
                  assemblyFolders={[]}
                  assemblyCategory=""
                  setAssemblyCategory={() => {}}
                  assemblyCategories={[]}
                  tailNumber={tailNumber}
                  setTailNumber={setTailNumber}
                  documentCategory={documentCategory}
                  setDocumentCategory={setDocumentCategory}
                  serialNumber={serialNumber}
                  setSerialNumber={setSerialNumber}
                  aircraftType={aircraftType}
                  setAircraftType={setAircraftType}
                  documentTitle={documentTitle}
                  setDocumentTitle={setDocumentTitle}
                  description={description}
                  setDescription={setDescription}
                  hideCategoryDropdownWhenTopLevel
                  hideScopeSelector
                  disabledFields={{
                    tailNumber: true,
                    documentCategory: true,
                    aircraftType: true,
                    documentTitle: true,
                    serialNumber: false,
                    description: false,
                  }}
                />
              </Box>

              {/* Right-side */}
              <Box
                sx={{
                  width: "60%",
                  height: "100%",
                  overflowY: "auto",
                  overflowX: "hidden",
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <DocumentViewer
                  selectedDoc={{
                    id: doc.id,
                    name: doc.fileName,
                    type: doc.mimeType ?? "",
                    size: doc.sizeBytes?.toString() ?? "",
                  }}
                  onClose={onClose}
                />
              </Box>
            </Box>

            {/* Fixed buttons at bottom */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                p: 2,
                borderTop: "1px solid rgba(255,255,255,0.2)",
                flexShrink: 0,
                bgcolor: "#1e1e1e",
              }}
            >
              <Button
                variant="outlined"
                sx={{ color: "#fff", borderColor: "#fff" }}
                onClick={onClose}
                disabled={saving}
              >
                Cancel
              </Button>

              <Button
                variant="contained"
                onClick={handleRetrySubmit}
                disabled={saving}
                sx={{
                  bgcolor: "hsl(19 101% 51% / 1)",
                  "&:hover": { bgcolor: "hsl(19 101% 41% / 1)" },
                }}
              >
                {saving ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  "Save & Retry"
                )}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default FailedDocumentModal;
